/**
 * Custom axios instance used by the Orval-generated improve-api client (the orval `mutator`).
 *
 * Routes every generated request through a dedicated axios instance so error normalization
 * (and any future cross-cutting behavior) is scoped to improve-api only — app1/Strapi keep
 * using the global default `axios` and are unaffected.
 *
 * Authored here in improve-api/client (the orval source of truth) and copied verbatim into
 * improve-ui by `npm run generate:local`. Treat the improve-ui copy as generated — do not edit.
 */
import Axios, { AxiosRequestConfig, AxiosResponse, isAxiosError } from "axios";

export const AXIOS_INSTANCE = Axios.create();

/**
 * Extract a user-facing message from an improve-api error.
 *
 * The NestJS error body is `{ statusCode, message, error, ... }`, where `message` is a string
 * or — for class-validator validation errors — a `string[]`. Falls back to the generic
 * axios/Error message, then to `fallback`.
 */
export function getApiErrorMessage(error: unknown, fallback = "Something went wrong"): string {
  if (isAxiosError(error)) {
    const data = error.response?.data;
    if (data && typeof data === "object" && "message" in data) {
      const msg = (data as { message: unknown }).message;
      if (typeof msg === "string") return msg;
      if (Array.isArray(msg)) return msg.filter((m) => typeof m === "string").join(", ");
    }
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return fallback;
}

// Surface the server's message as `error.message` for every improve-api request, so any caller
// reading `error.message` (snackbars, etc.) gets the real message instead of axios's generic
// "Request failed with status code 4xx". Response bodies are left untouched.
AXIOS_INSTANCE.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (isAxiosError(error)) {
      error.message = getApiErrorMessage(error);
    }
    return Promise.reject(error);
  }
);

/**
 * Orval mutator. Orval calls this as `customInstance<DataType>(...)`, so we return the full
 * `Promise<AxiosResponse<T>>` (NOT unwrapped to `.data`). The generated functions infer their
 * return type from this signature, which keeps every caller's `response.data` valid at type and
 * runtime. The second argument is the per-request config the UI passes (baseURL + auth header
 * from `useImproveAxios`); its headers are merged so the endpoint's `Content-Type` is preserved.
 */
export const customInstance = <T>(config: AxiosRequestConfig, options?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
  return AXIOS_INSTANCE<T>({
    ...config,
    ...options,
    headers: { ...config.headers, ...options?.headers },
  });
};
